/**
 * Shared utilities, constants, and styles for SwipePanel components.
 */
import { css, SerializedStyles } from "@emotion/react";
import { styleUtils, theme } from "@minted/minted-components";
import React, { useCallback, useState } from "react";

export const HEADER_HEIGHT = 32;
export const SWIPE_EXCESS_HEIGHT = 32;

export const lockBodyScroll = (): void => {
  document.body.style.overflow = "hidden";
  document.body.style.touchAction = "none";
  document.body.style.overscrollBehavior = "none";
};

export const unlockBodyScroll = (): void => {
  document.body.style.overflow = "";
  document.body.style.touchAction = "";
  document.body.style.overscrollBehavior = "";
};

export enum SwipeDirection {
  Up = "up",
  Down = "down",
  Left = "left",
  Right = "right",
}

export type Position = {
  x: number;
  y: number;
};

type UseSwipeGestureProps = {
  allowedDirections?: SwipeDirection[];
  currentPosition: number;
  minPosition: number;
  onPositionChange: (position: number) => void;
  onSwipeEnd?: (direction: SwipeDirection) => void;
  onSwipingChange: (isSwiping: boolean) => void;
};

type UseSwipeGestureReturn = {
  dragDelta: Position;
  handleScrollableChild: (event: React.UIEvent) => void;
  handleStartSwipe: (event: React.MouseEvent | React.TouchEvent) => void;
  isSwiping: boolean;
};

/**
 * Custom hook that handles touch and mouse swipe gestures for panels.
 * Manages real-time position updates during dragging and determines final position on release.
 */
export const useSwipeGesture = ({
  allowedDirections = [SwipeDirection.Down],
  currentPosition,
  minPosition,
  onPositionChange,
  onSwipeEnd,
  onSwipingChange,
}: UseSwipeGestureProps): UseSwipeGestureReturn => {
  const [dragDelta, setDragDelta] = useState<Position>({
    x: 0,
    y: 0,
  });
  const [isSwiping, setIsSwiping] = useState(false);

  const handleStartSwipe = useCallback(
    (event: React.MouseEvent | React.TouchEvent) => {
      event.preventDefault();
      event.stopPropagation();

      const clientX =
        "touches" in event ? event.touches[0].clientX : event.clientX;
      const clientY =
        "touches" in event ? event.touches[0].clientY : event.clientY;

      setIsSwiping(true);
      onSwipingChange(true);
      setDragDelta({
        x: 0,
        y: 0,
      });

      const handleMove = (moveEvent: MouseEvent | TouchEvent) => {
        moveEvent.preventDefault();

        const currentX =
          "touches" in moveEvent
            ? moveEvent.touches[0].clientX
            : moveEvent.clientX;
        const currentY =
          "touches" in moveEvent
            ? moveEvent.touches[0].clientY
            : moveEvent.clientY;

        const deltaX = currentX - clientX;
        const deltaY = currentY - clientY;

        setDragDelta({
          x: deltaX,
          y: deltaY,
        });

        // Update position in real-time (negative deltaY because swiping up should increase position)
        const newPosition = Math.max(minPosition, currentPosition - deltaY);
        onPositionChange(newPosition);
      };

      const handleEnd = (endEvent: MouseEvent | TouchEvent) => {
        const endX =
          "changedTouches" in endEvent
            ? endEvent.changedTouches[0].clientX
            : endEvent.clientX;
        const endY =
          "changedTouches" in endEvent
            ? endEvent.changedTouches[0].clientY
            : endEvent.clientY;

        const deltaX = endX - clientX;
        const deltaY = endY - clientY;
        const finalPosition = Math.max(minPosition, currentPosition - deltaY);

        const threshold = 50;
        let direction: SwipeDirection | null = null;

        if (Math.abs(deltaY) > Math.abs(deltaX)) {
          if (deltaY < -threshold) {
            direction = SwipeDirection.Up;
          }
          if (deltaY > threshold) {
            direction = SwipeDirection.Down;
          }
        } else {
          if (deltaX < -threshold) {
            direction = SwipeDirection.Left;
          }
          if (deltaX > threshold) {
            direction = SwipeDirection.Right;
          }
        }

        onPositionChange(finalPosition);

        if (direction && allowedDirections.includes(direction) && onSwipeEnd) {
          onSwipeEnd(direction);
        }

        setIsSwiping(false);
        onSwipingChange(false);
        setDragDelta({
          x: 0,
          y: 0,
        });

        document.removeEventListener("mousemove", handleMove);
        document.removeEventListener("mouseup", handleEnd);
        document.removeEventListener("touchmove", handleMove);
        document.removeEventListener("touchend", handleEnd);
      };

      document.addEventListener("mousemove", handleMove);
      document.addEventListener("mouseup", handleEnd);
      document.addEventListener("touchmove", handleMove);
      document.addEventListener("touchend", handleEnd);
    },
    [
      allowedDirections,
      currentPosition,
      minPosition,
      onPositionChange,
      onSwipeEnd,
      onSwipingChange,
    ],
  );

  const handleScrollableChild = useCallback((event: React.UIEvent) => {
    event.stopPropagation();
  }, []);

  return {
    dragDelta,
    handleScrollableChild,
    handleStartSwipe,
    isSwiping,
  };
};

export const swipePanelSharedStyles = {
  container: (
    currentPosition: number,
    isSwiping: boolean,
  ): SerializedStyles => css`
    background: ${theme.colors.white};
    border-top-left-radius: ${theme.spacing.x3};
    border-top-right-radius: ${theme.spacing.x3};
    bottom: -${SWIPE_EXCESS_HEIGHT}px;
    box-shadow: 0 2px 11px rgba(0, 0, 0, 0.15);
    display: flex;
    flex-direction: column;
    height: ${SWIPE_EXCESS_HEIGHT + currentPosition}px;
    left: 50%;
    max-height: 100vh;
    padding-bottom: ${SWIPE_EXCESS_HEIGHT}px;
    position: fixed;
    touch-action: pan-y;
    transform: translate3d(-50%, 100%, 0);
    transition: transform 0.3s ease;
    width: 100%;
    z-index: 1001;

    @media (pointer: fine) {
      &:active {
        transition: none;
      }
    }

    ${theme.media.greaterThan("large")(css`
      max-width: 25rem;
    `)}

    ${isSwiping &&
    css`
      touch-action: none;
      transition: none;
    `}
  `,
  content: css`
    background-color: ${theme.colors.white};
    color: ${theme.colors.gray800};
    flex: 1;
    min-height: 0;
    padding-left: ${theme.spacing.x5};
    padding-right: ${theme.spacing.x5};
  `,
  contentScrollable: css`
    overflow-y: auto;
    overscroll-behavior: none;
  `,
  contentWrapper: css`
    background-color: ${theme.colors.white};
    min-height: 100%;
    width: 100%;
  `,
  scrim: css`
    bottom: 0;
    left: 0;
    pointer-events: none;
    position: fixed;
    right: 0;
    top: 0;
    z-index: 999;
  `,
  scrimVisible: css`
    pointer-events: all;
  `,
  swipeArea: css`
    align-items: center;
    display: flex;
    height: ${styleUtils.rem(HEADER_HEIGHT)};
    justify-content: center;
    position: relative;
    width: 100%;
  `,
  swipeHandle: css`
    background: ${theme.colors.gray500};
    border-radius: ${theme.spacing.base};
    height: ${theme.spacing.base};
    left: 50%;
    position: absolute;
    top: ${theme.spacing.x2};
    transform: translateX(-50%);
    width: ${theme.spacing.x8};
  `,
};

// Animation helper for the panel movement
export const getPanelTransform = (
  currentPosition: number,
  isSwiping: boolean,
): React.CSSProperties => ({
  transform: `translate3d(-50%, calc(100% - ${SWIPE_EXCESS_HEIGHT + currentPosition}px), 0)`,
  transition: isSwiping ? "none" : "transform 0.3s ease",
});
